/**
 * The import profiles offered on New Tax Roll > Import & Balance, and which assessor company
 * each one belongs to.
 *
 * The county picks its Assessor Company first and is then offered only that company's profiles,
 * so a Landmark county is never shown ACT's tax roll and vice versa. `companies` is a list
 * because several of these files do not come from the assessor at all - the Special Assessment
 * CSVs and the fire tax files are the county's own - and those are offered under every company
 * that can use them. Adding a company-specific profile later is an entry in this list, not a
 * code change.
 *
 * `value` is the TransformMapper profile name and is what the backend actually imports on, so
 * these must keep matching the seeded profile names in
 * tmc-local-backend/src/mongo/models/config/seedData/tax/accessorFiles (which carry the same
 * `assessorCompany` on the mapper). `name` is display only, and is deliberately company-neutral
 * - 'Taxroll', not 'Landmark Taxroll' - because the company is now named in its own dropdown
 * directly above, and because a shared profile would otherwise be shown under another company's
 * name (ACT reads Landmark's mobile home file).
 *
 * `notSupported` marks a profile with no working parser behind it. These are listed and greyed
 * out rather than hidden, so the gap is visible as a gap instead of looking like a format we
 * never offered.
 */
const ASSESSOR_COMPANIES = ['Landmark', 'ACT', 'Apprentice', 'Patriot', 'Tulsa'];

/**
 * The companies whose files arrive on the Landmark FTP (lmweb.dyndns.org), which keeps one
 * folder per county it serves. ACT is on the list because an ACT county still reads Landmark's
 * mobile home file - ACT has none of its own.
 *
 * Tulsa and Apprentice are not: Tulsa's assessor is in-house, so there is no tulsa folder and
 * there never will be, and the listing came back 550 on every Import & Balance page load during
 * the conversion. Anything not in ASSESSOR_COMPANIES - including a county that has not filled
 * the setting in yet, which is most of them - counts as unknown and keeps the box, because a
 * blank setting must not take the FTP away from a Landmark county that never set it.
 */
const LANDMARK_FTP_COMPANIES = ['Landmark', 'ACT'];

const usesLandmarkFtp = (company) => !ASSESSOR_COMPANIES.includes(company)
  || LANDMARK_FTP_COMPANIES.includes(company);

const assesorList = [
  { name: 'Taxroll', value: 'Assessor Tax File Import', companies: ['Landmark'] },
  // ACT copies Landmark's file layout but needed its own profile so that ACT's handling - blank
  // personal property parcel ids, padded situs numbers, all-zero zips, acres rounding and the
  // care-of/address lines - cannot reach a Landmark county. Johnston is the first ACT county.
  { name: 'Taxroll', value: 'ACT Taxroll Import', companies: ['ACT'] },
  { name: 'Taxroll', value: 'Assessor Tulsa Tax File Import', companies: ['Tulsa'] },
  // Apprentice writes Landmark's layout too, but formats five of the fields differently - mills
  // with the decimal point taken out, tax in whole dollars, acres and lots as literal numbers,
  // and a real estate mobile home value that is outside the gross - so it has a profile of its
  // own for the same reason ACT does. Grady is the first Apprentice county.
  { name: 'Taxroll', value: 'Apprentice Taxroll Import', companies: ['Apprentice'] },
  /* Patriot Properties is the first company whose file is not Landmark's layout at all - it is
     pipe-delimited with a header row and 66 named columns - so its two profiles are the only
     ones a Patriot county is offered. Payne is the first Patriot county. */
  { name: 'Taxroll', value: 'Assessor Patriot Tax File Import', companies: ['Patriot'] },
  // Patriot's own mobile home extract. A Patriot county cannot read Landmark's MH file the way
  // an ACT county does - the layouts have nothing in common.
  { name: 'Mobile Homes', value: 'Assessor Patriot MH File Import', companies: ['Patriot'] },
  /* Apprentice's fire district file. It is its own profile rather than one of the three that
     existed: the generic Fire Tax profile writes nothing, and Wagoner's two match by parcel
     through assessorfiretaxxrefs, which an Apprentice county has no rows in. Imported AFTER the
     roll - it adds the assessor's fire tax to what the roll staged. */
  { name: 'Fire District', value: 'Apprentice Fire District Import', companies: ['Apprentice'] },
  // ACT has no mobile home file of its own, so it reads Landmark's.
  { name: 'Mobile Homes', value: 'Assessor MH File Import', companies: ['Landmark', 'ACT'] },
  {
    name: 'Special Assessment',
    value: 'Assessor Tulsa Special Assessment File Import',
    companies: ['Tulsa'],
  },
  {
    name: 'Special Assessment CSV - Header',
    value: 'Assessor Special Assessment CSV (Header)',
    companies: ['Landmark', 'ACT', 'Apprentice', 'Patriot'],
  },
  {
    name: 'Special Assessment CSV - Headerless',
    value: 'Assessor Special Assessment CSV (Headerless)',
    companies: ['Landmark', 'ACT', 'Apprentice', 'Patriot'],
  },
  // The generic fire tax profile transforms its file and then writes nothing - the bulkWrite in
  // its destination is commented out (see importBackend.builder.js). It imports "successfully"
  // and changes no record, so it must not be offered as though it works.
  {
    name: 'Fire Tax',
    value: 'Assessor Fire Tax File Import',
    companies: ['Landmark', 'ACT', 'Apprentice'],
    notSupported: true,
  },
  // Wagoner's two fire tax files do not come from the assessor either; they are listed under
  // Landmark because Wagoner is a Landmark county.
  {
    name: 'Fire Tax (Wagoner County)',
    value: 'Assessor Fire Tax File Import(Wagoner County)',
    companies: ['Landmark'],
  },
  {
    name: 'Extra Fire Tax (Wagoner County)',
    value: 'Assessor Extra Fire Tax File Import(Wagoner County)',
    companies: ['Landmark'],
  },
];

/**
 * The profiles to offer for one company, in list order. An unknown or unchosen company gets
 * nothing - the profile dropdown stays empty and disabled until a company is picked, rather
 * than defaulting to a vendor the county may not be on.
 *
 * The "not yet supported" suffix is added here instead of in the data so the entry still reads
 * as its plain name everywhere else.
 */
const profilesForCompany = (company) => assesorList
  .filter((profile) => (profile.companies || []).includes(company))
  .map((profile) => (profile.notSupported
    ? { ...profile, name: `${profile.name} - not yet supported` }
    : profile));

const assesorListTwo = [
  { name: 'Accessor Tax', value: 'Assessor Tax File Import' },
  { name: 'Accessor MH', value: 'Accessor MH' },
];

export {
  ASSESSOR_COMPANIES,
  LANDMARK_FTP_COMPANIES,
  assesorList,
  assesorListTwo,
  profilesForCompany,
  usesLandmarkFtp,
};
